/* eslint-disable no-undef */
import React, { useEffect, useState, useMemo, useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { debounce } from "lodash";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

function useDebouncedFetch(fetchFn, dependencies, delay = 300) {
  const debouncedFetch = useMemo(
    () => debounce(fetchFn, delay),
    [fetchFn, delay]
  );

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, dependencies);
}

function GetEmployee() {
  const [employee, setEmployee] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);
  const [employeeCount, setEmployeeCount] = useState("Loading...");
  const [showDetails, setShowDetails] = useState({});
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [taskFilterMin, setTaskFilterMin] = useState("");
  const [taskFilterMax, setTaskFilterMax] = useState("");
  const [earningFilterMin, setEarningFilterMin] = useState("");
  const [earningFilterMax, setEarningFilterMax] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedShop, setSelectedShop] = useState("");
  const [shops, setShops] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredShops, setFilteredShops] = useState([]);
  const [shopSearchTerm, setShopSearchTerm] = useState("");
  const [isShopOpen, setIsShopOpen] = useState(false);
  const limit = 50;

  const shopDropdownRef = useRef(null);

  // Close shop dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(event.target)) {
        setIsShopOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch all shops once
  const fetchAllShops = () => {
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetch(`https://visitguru.in/api/v1/employee/getEmp?page=1`, {
      headers,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          const allShopNames = [...new Set(data.data.map((emp) => {
            const shopName = typeof emp.shopName === 'object' ? (emp.shopName?.name || emp.shopName?.shopName) : emp.shopName;
            return shopName;
          }).filter(Boolean))];
          const uniqueShops = allShopNames.map((name, index) => ({
            _id: index.toString(),
            name,
          }));
          setShops(uniqueShops);
        }
      })
      .catch((error) => {
        console.error("Error fetching all shops:", error);
      });
  };

  // Fetch all filtered employees
  const fetchEmployeeData = async () => {
    setLoading(true);
    setError(null);

    const hasFilters = selectedCategory || taskFilterMin || taskFilterMax ||
      earningFilterMin || earningFilterMax || fromDate || toDate || searchTerm;

    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    try {
      let allData = [];
      let currentPage = 1;
      let totalPages = 1;

      // Fetch all pages if filters are active
      if (hasFilters) {
        do {
          const queryParams = new URLSearchParams({
            page: currentPage,
          });

          if (searchTerm) queryParams.append('search', searchTerm);
          if (selectedCategory) queryParams.append('category', selectedCategory);
          if (taskFilterMin) queryParams.append('minTask', taskFilterMin);
          if (taskFilterMax) queryParams.append('maxTask', taskFilterMax);
          if (earningFilterMin) queryParams.append('minEarning', earningFilterMin);
          if (earningFilterMax) queryParams.append('maxEarning', earningFilterMax);
          if (fromDate) {
            const [year, month, day] = fromDate.split('-');
            queryParams.append('fromDate', `${day}-${month}-${year}`);
          }
          if (toDate) {
            const [year, month, day] = toDate.split('-');
            queryParams.append('toDate', `${day}-${month}-${year}`);
          }

          const response = await fetch(`https://visitguru.in/api/v1/employee/getEmp?${queryParams}`, { headers });
          if (!response.ok) throw new Error(`Network error: ${response.status}`);
          
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            allData = [...allData, ...data.data];
            totalPages = data.pagination?.totalPages || 1;
            currentPage++;
          } else {
            break;
          }
        } while (currentPage <= totalPages);

        const mappedEmployees = allData.map((emp) => ({
          ...emp,
          mobile: emp.vendorMobile || emp.mobile || emp.phone || emp.phoneNumber,
          shopName: typeof emp.shopName === 'object' ? (emp.shopName?.name || emp.shopName?.shopName || 'N/A') : (emp.shopName || 'N/A'),
          totalTasks: emp.completedTasks ?? 0,
          totalEarning: emp.completedEarnings ?? 0,
          totalAssignedTasks: emp.totalAssignedTasks ?? 0,
        }));

        setAllEmployees(mappedEmployees);
        setEmployeeCount(mappedEmployees.length);
        setTotalPages(Math.ceil(mappedEmployees.length / limit));
      } else {
        // No filters - use normal pagination
        const queryParams = new URLSearchParams({ page });
        if (searchTerm) queryParams.append('search', searchTerm);
        const response = await fetch(`https://visitguru.in/api/v1/employee/getEmp?${queryParams}`, { headers });
        if (!response.ok) throw new Error(`Network error: ${response.status}`);
        
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          const mappedEmployees = data.data.map((emp) => ({
            ...emp,
            mobile: emp.vendorMobile || emp.mobile || emp.phone || emp.phoneNumber,
            shopName: typeof emp.shopName === 'object' ? (emp.shopName?.name || emp.shopName?.shopName || 'N/A') : (emp.shopName || 'N/A'),
            totalTasks: emp.completedTasks ?? 0,
            totalEarning: emp.completedEarnings ?? 0,
            totalAssignedTasks: emp.totalAssignedTasks ?? 0,
          }));

          setAllEmployees([]);
          setEmployee(mappedEmployees);
          setEmployeeCount(data.pagination?.totalRecords || mappedEmployees.length);
          setTotalPages(data.pagination?.totalPages || 1);
        }
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error(`Error: ${error.message}`);
      setEmployee([]);
      setEmployeeCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategoryData = () => {
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetch("https://visitguru.in/api/v1/category/getCategory", {
      headers
    })
      .then((res) => {
        console.log("Category fetch response:", res);
        if (res.status === 404) {
          throw new Error("Category endpoint not found (404)");
        }
        if (!res.ok) {
          throw new Error("Failed to fetch categories");
        }
        return res.json();
      })
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          const normalized = data.data.map((cat) => ({
            _id: cat._id,
            name: cat.name || cat.categoryName || cat.title || "Unnamed",
          }));
          setCategories(normalized);
        } else {
          toast.error("Failed to load categories");
        }
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        toast.error(`Error fetching categories: ${err.message}`);
      });
  };

  // Fetch data when page changes (only if no filters)
  useEffect(() => {
    const hasFilters = selectedCategory || taskFilterMin || taskFilterMax ||
      earningFilterMin || earningFilterMax || fromDate || toDate;
    if (!hasFilters) {
      fetchEmployeeData();
    }
  }, [page]);

  // Fetch when backend filters change
  useEffect(() => {
    if (page === 1) {
      fetchEmployeeData();
    } else {
      setPage(1);
    }
  }, [
    searchTerm,
    taskFilterMin,
    taskFilterMax,
    earningFilterMin,
    earningFilterMax,
    selectedCategory,
    fromDate,
    toDate,
  ]);

  // Debounce for frontend filters
  useDebouncedFetch(() => {
    // Frontend filters don't need API call
  }, [selectedShop]);

  useEffect(() => {
    setFilteredShops(shops);
  }, [shops]);

  useEffect(() => {
    if (shopSearchTerm) {
      const results = shops.filter((shop) =>
        shop.name.toLowerCase().includes(shopSearchTerm.toLowerCase())
      );
      setFilteredShops(results);
    } else {
      setFilteredShops(shops);
    }
  }, [shopSearchTerm, shops]);

  // Fetch categories and all shops on component mount
  useEffect(() => {
    fetchCategoryData();
    fetchAllShops();
  }, []);

  // Handle filtering and pagination
  const filteredEmployees = useMemo(() => {
    const hasFilters = selectedCategory || taskFilterMin || taskFilterMax ||
      earningFilterMin || earningFilterMax || fromDate || toDate || searchTerm;
    
    let result = hasFilters ? allEmployees : employee;
    result = result.filter((emp) => emp.status !== "Suspended");

    // Apply frontend-only filter (shop)
    if (selectedShop) {
      result = result.filter((emp) => {
        const shopName = String(emp.shopName || "");
        return shopName.toLowerCase().includes(selectedShop.toLowerCase());
      });
    }

    // Apply frontend pagination when filters are active
    if (hasFilters) {
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      return result.slice(startIndex, endIndex);
    }

    return result;
  }, [employee, allEmployees, searchTerm, selectedShop, selectedCategory, taskFilterMin, taskFilterMax, earningFilterMin, earningFilterMax, fromDate, toDate, page]);

  const toggleDetails = (id) => {
    setShowDetails((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handlePrev = () => {
    if (page > 1) {
      setLoading(true);
      setTimeout(() => {
        setPage(page - 1);
        setLoading(false);
      }, 1000);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setLoading(true);
      setTimeout(() => {
        setPage(page + 1);
        setLoading(false);
      }, 1000);
    }
  };

  const handleDelete = async (employeeId) => {
    if (!employeeId) {
      toast.error("No employee ID provided");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this employee?"
    );
    if (!confirmed) {
      toast.info("Deletion cancelled");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(
        `https://visitguru.in/api/v1/employee/employee/${employeeId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      const result = await res.json();
      if (res.ok && result.success) {
        toast.success("Employee deleted successfully!");
        fetchEmployeeData();
      } else {
        toast.error(result.message || "Failed to delete employee");
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Server error while deleting employee.");
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    doc.setFontSize(14);
    doc.text("All Employee Records", 14, 15);

    const tableData = filteredEmployees.map((emp, index) => [
      index + 1,
      emp.firstName || "N/A",
      emp.lastName || "N/A",
      emp.mobile || "N/A",
      emp.shopName || "N/A",
      emp.totalAssignedTasks ?? 0,
    ]);

    autoTable(doc, {
      startY: 20,
      head: [
        ["#", "First Name", "Last Name", "Mobile", "Shop Name",],
      ],
      body: tableData,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [8, 132, 196], textColor: [255, 255, 255] },
    });

    doc.save("employee_list.pdf");
  };

  if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
      />
      <div className="px-4 py-3 border-b">
        <h3 className="font-semibold text-base text-blueGray-700 mb-2">
          Employees{" "}
          <span className="ml-2 text-sm text-blueGray">
            (Total Employees: {filteredCount > 0 ? filteredCount : employeeCount})
          </span>
          <button
            onClick={generatePDF}
            style={{
              backgroundColor: "#0884c4",
              float: "right",
              width: "100px",
              height: "35px",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              outline: "none",
              marginTop: "-4px",
            }}
          >
            Download
          </button>
        </h3>

        <input
          type="text"
          placeholder="Search by any field (name, mobile, shop, etc.)…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="w-full md:w-1/3 px-3 py-2 border border-blueGray-200 rounded focus:outline-none focus:ring mb-2"
        />

        <div style={{ gap: "15px" }} className="flex flex-wrap gap-4 mb-3">
          <div>
            <label className="block text-xs font-semibold mb-1">Min Tasks</label>
            <input
              style={{ width: "70px" }}
              type="number"
              min="0"
              placeholder="0"
              value={taskFilterMin}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || parseInt(value, 10) >= 0) {
                  setTaskFilterMin(value);
                  setPage(1);
                } else {
                  toast.error("Min Tasks cannot be negative");
                }
              }}
              className="w-24 px-2 py-1 border border-blueGray-200 rounded focus:outline-none focus:ring"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Max Tasks</label>
            <input
              style={{ width: "70px" }}
              type="number"
              min="0"
              placeholder="∞"
              value={taskFilterMax}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || parseInt(value, 10) >= 0) {
                  setTaskFilterMax(value);
                  setPage(1);
                } else {
                  toast.error("Max Tasks cannot be negative");
                }
              }}
              className="w-24 px-2 py-1 border border-blueGray-200 rounded focus:outline-none focus:ring"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">
              Min Earning
            </label>
            <input
              style={{ width: "70px" }}
              type="number"
              min="0"
              step="0.01"
              placeholder="0"
              value={earningFilterMin}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || parseFloat(value) >= 0) {
                  setEarningFilterMin(value);
                  setPage(1);
                } else {
                  toast.error("Min Earning cannot be negative");
                }
              }}
              className="w-28 px-2 py-1 border border-blueGray-200 rounded focus:outline-none focus:ring"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">
              Max Earning
            </label>
            <input
              style={{ width: "70px" }}
              type="number"
              min="0"
              step="0.01"
              placeholder="∞"
              value={earningFilterMax}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || parseFloat(value) >= 0) {
                  setEarningFilterMax(value);
                  setPage(1);
                } else {
                  toast.error("Max Earning cannot be negative");
                }
              }}
              className="w-28 px-2 py-1 border border-blueGray-200 rounded focus:outline-none focus:ring"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setPage(1);
              }}
              className="w-48 px-2 py-1 border border-blueGray-200 rounded focus:outline-none focus:ring"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative" ref={shopDropdownRef}>
            <label className="block text-xs font-semibold mb-1">Vendor</label>
            <div
              className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring"
              style={{ width: "200px", height: "38px" }}
              onClick={() => setIsShopOpen(!isShopOpen)}
            >
              {selectedShop || "Select Shop"}
            </div>
            {isShopOpen && (
              <div
                className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  width: "200px"
                }}
              >
                <input
                  type="text"
                  placeholder="Search shop..."
                  value={shopSearchTerm}
                  onChange={(e) => setShopSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                />
                <ul>
                  <li
                    className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                    onClick={() => {
                      setSelectedShop("");
                      setIsShopOpen(false);
                      setPage(1);
                    }}
                  >
                    All Shops
                  </li>
                  {filteredShops.length > 0 ? (
                    filteredShops.map((shop) => (
                      <li
                        key={shop._id}
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                        onClick={() => {
                          console.log('Selecting shop:', shop.name);
                          setSelectedShop(shop.name);
                          setIsShopOpen(false);
                          setPage(1);
                        }}
                      >
                        {shop.name}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500">
                      No shops found
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="w-40 border rounded-md px-2 py-1 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="w-40 border rounded-md px-2 py-1 text-sm"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm("");
                setTaskFilterMin("");
                setTaskFilterMax("");
                setEarningFilterMin("");
                setEarningFilterMax("");
                setSelectedCategory("");
                setSelectedShop("");
                setShopSearchTerm("");
                setFromDate("");
                setToDate("");
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full bg-transparent border-collapse">
          <thead>
            <tr>
              {[
                "#",
                "First Name",
                "Last Name",
                "Mobile",
                "Shop Name",
                "Created At",
               
                "Actions",
              ].map((head, i) => (
                <th
                  key={i}
                  className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left"
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? (
                <tr>
                  <td
                    colSpan="8"
                    className="p-8 text-center text-lg text-blueGray-600"
                  >
                    Loading...
                  </td>
                </tr>
              )
              : filteredEmployees.map((emp, index) => {
                if (!emp || typeof emp !== 'object') return null;
                const deleteId = emp._id || emp.id || emp.empId || emp.mobile || index;
                const id = String(emp._id || emp.mobile || index);
                return (
                  <React.Fragment key={id}>
                    <tr>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {String((page - 1) * limit + index + 1)}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {String(emp?.firstName || "N/A")}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {String(emp?.lastName || "N/A")}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {String(emp?.mobile || "N/A")}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {String(emp?.shopName || "N/A")}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {(() => {
                          const dateField = emp?.createdAt || emp?.created_at || emp?.dateCreated || emp?.registrationDate;
                          return dateField ? String(new Date(dateField).toLocaleDateString()) : "N/A";
                        })()}
                      </td>
                      {/* <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {String(emp?.totalAssignedTasks ?? 0)}
                      </td> */}
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100 flex gap-2">
                        <button
                          style={{
                            fontSize: "12px",
                            height: "40px",
                          }}
                          onClick={() => toggleDetails(id)}
                          className="bg-lightBlue-500 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow mr-2"
                        >
                          {showDetails[id] ? "Hide Details" : "View Details"}
                        </button>
                        <button
                          style={{
                            fontSize: "12px",
                            height: "40px",
                            justifyContent: "center",
                          }}
                          onClick={() => handleDelete(deleteId)}
                          className="text-white bg-red-600 hover:bg-red-700 font-bold ml-2 px-3 py-1 rounded"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {showDetails[id] && (
                      <tr>
                        <td colSpan="8" className="px-6 pb-4">
                          <div className="bg-white shadow rounded p-4 mt-2 border border-gray-200">
                            <h4 className="font-semibold text-lg mb-2 text-gray-800">
                              {String(emp?.firstName || "N/A")} {String(emp?.lastName || "")}
                            </h4>
                            {emp?.mobile && (
                              <p>
                                <strong>Mobile:</strong> {String(emp.mobile)}
                              </p>
                            )}
                            <p>
                              <strong>Address:</strong> {String(emp?.address || "N/A")}
                            </p>
                            <p>
                              <strong>Shop Name:</strong>{" "}
                              {String(emp?.shopName || "N/A")}
                            </p>
                            <p>
                              <strong>Categories:</strong>{" "}
                              {Array.isArray(emp?.category) && emp.category.length > 0
                                ? emp.category.map(cat => 
                                    typeof cat === 'object' ? (cat.categoryName || cat.name || 'N/A') : cat
                                  ).join(", ")
                                : "N/A"}
                            </p>
                            <p>
                              <strong>Created At:</strong>{" "}
                              {emp?.createdAt
                                ? String(new Date(emp.createdAt).toLocaleString())
                                : "N/A"}
                            </p>
                            <p>
                              <strong>Status:</strong> {String(emp?.status || "N/A")}
                            </p>
                            <p>
                              <strong>Total Completed Tasks:</strong>{" "}
                              {String(emp?.totalTasks ?? 0)}
                            </p>
                            <p>
                              <strong>Total Earning:</strong> ₹
                              {String((emp?.totalEarning ?? 0).toFixed(2))}
                            </p>
                            {/* <p>
                              <strong>Total Assigned Tasks:</strong>{" "}
                              {String(emp?.totalAssignedTasks ?? 0)}
                            </p> */}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            {!loading && filteredEmployees.length === 0 && (
              <tr>
                <td
                  colSpan="8"
                  className="p-8 text-center text-lg text-blueGray-500"
                >
                  No employees found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center p-4 border-t">
        <button
          disabled={page === 1 || loading}
          onClick={handlePrev}
          className="px-4 py-2 bg-gray-300 text-sm rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span className="text-sm">
          Page {page} of {totalPages} (Showing {filteredEmployees.length} of {employeeCount} total employees)
        </span>
        <button
          disabled={page === totalPages || loading}
          onClick={handleNext}
          className="px-4 py-2 bg-gray-300 text-sm rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default GetEmployee;